import { Component, OnDestroy, OnInit } from '@angular/core';

import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import {
  CompanyLookupResponseModel,
  CompanySearchResponseModel,
  AddCompanyRequestModel
} from 'src/app/models/company.model';
import {
  AddContactRequestModel
} from 'src/app/models/contacts.model';
import { CompanyApiService } from 'src/app/services/api/company-api.service';
import { TranslateService } from '@ngx-translate/core';
import { phoneNumberValidator } from 'src/app/validators/phoneNumber';
import { debounceTime } from 'rxjs/operators';
import { handleApiError } from 'src/app/utils/error';
import { reasonOptions } from 'src/configs/dropdown-options/options.se';
import { Subscription } from 'rxjs';
import { TrackingService } from 'src/app/services/tracking.service';
import { FlowService } from 'src/app/services/flow.service';
import { Flows } from 'src/app/app.consts';
import { environment } from 'src/environments/local/environment';
import { ResellerApiService } from 'src/app/services/api/reseller-api.service';
import { Title } from '@angular/platform-browser';

declare type FormValuesType = {
  id: string;
  amount: number;
  duration: number;
  email: string;
  phoneNumber: string;
  clientId: string;
  reason: string;
  name: string;
};

@Component({
  selector: 'app-reseller',
  templateUrl: './reseller.component.html',
  styleUrls: ['./reseller.component.scss'],
})
export class ResellerComponent implements OnInit, OnDestroy {
  error = '';
  resellerForm!: FormGroup;
  companies!: Array<CompanySearchResponseModel>;
  leadCreated = false;
  leadId = '';
  reasonOptions = reasonOptions;
  subs = Array<Subscription>();
  loading = false;
  searchSelected = false;
  tosLink = environment.tos;
  resellerTosLink= environment.tosReseller;
  contact = {} as AddContactRequestModel;
  company = {} as CompanyLookupResponseModel;
  resellerRegistered = false;
  trackingUrl = '';
  // trackingUrl= 'https://creddo.com/se/reseller/123456';

  constructor(
    private formBuilder: FormBuilder,
    private companyApi: CompanyApiService,
    private titleService: Title,
    private translateService: TranslateService,
    private trackingService: TrackingService,
    public flowService: FlowService,
    private resellerApi: ResellerApiService,
  ) {}

  ngOnInit(): void {
    this.flowService.selectFlow(Flows.INITIAL);
    this.titleService.setTitle(this.translateService.instant('reseller.pageTitle'));
    this.resellerForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, phoneNumberValidator()]],
      companyName: ['', [Validators.required]],
      name: ['', [Validators.required]],
    });

    this.subs.push(
      this.companyName.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((v) => {
          if (v.length > 2) {
            this.loading = true;
            this.companyApi.companySearch(v).subscribe(
              (response: CompanySearchResponseModel[]) => {
                this.loading = false;
                this.companies = response;
              },
              (error: any) => {
                this.error = handleApiError(error, this.translateService);
                this.loading = false;
              }
            );
            if (v === 'cannotFindCompany') {
              this.companies = [];
              this.companyName.patchValue('');
            }
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }

  getFormValues(id?: string): FormValuesType {
    const values: { [key: string]: string | number } = {
      id: id ? id : '',
    };

    Object.keys(this.form.controls).map((key) => {
      const control = this.form.get(key) as FormControl;
      if (control.valid) {
        if (key === 'companyName' && typeof control.value === 'object') {
          values[key] = control.value.companyName;
        } else {
          values[key] = control.value;
        }
      }
    });

    if (values.phoneNumber && typeof values.phoneNumber === 'string') {
      values.phoneNumber = values.phoneNumber
        .replace(/\s/g, '')
        .replace(/[+]/g, '')
        .replace(/-/g, '');
    }

    return values as FormValuesType;
  }

  trackFields(): void {
    if (this.email.valid) {
      this.trackingService.filledField('reseller-email');
    }
    if (this.phoneNumber.valid) {
      this.trackingService.filledField('reseller-phoneNumber');
    }
    if (this.companyName.valid) {
      this.trackingService.filledField('reseller-company');
    }
    if (this.name.valid) {
      this.trackingService.filledField('reseller-name');
    }
  }

  get form(): FormGroup {
    return this.resellerForm;
  }

  get email(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get phoneNumber(): FormControl {
    return this.form.get('phoneNumber') as FormControl;
  }

  get companyName(): FormControl {
    return this.form.get('companyName') as FormControl;
  }

  get name(): FormControl {
    return this.form.get('name') as FormControl;
  }

  handleButtonClick(): void {
    this.trackFields();

    if (this.form.invalid) {
      return;
    }
    if (!this.searchSelected) {
      this.companyName.setErrors({ companySearchRequired: true });
      return;
    }
    this.subs.push(
      this.companyApi
        .companyLookup(this.companyName.value.companyId)
        .subscribe(
          (resp: CompanyLookupResponseModel) => {
            this.populateData(resp);
            this.postData(this.contact.firstname, this.contact.lastname);
          },
          (error: any) => handleApiError(error, this.translateService)
        )
    );
  }

  populateData(lookupData: CompanyLookupResponseModel): void {
    const formValues = this.getFormValues();
    this.contact.email = formValues.email;
    this.contact.phoneNumber = formValues.phoneNumber;
    const name = formValues.name.split(' ');
    this.contact.firstname = name[0];
    if (name.length > 1) {
      this.contact.lastname = name[1];
    }
    this.company = lookupData;
  }

  public postData(
    firstname?: string,
    lastname?: string,
  ): void {
    const addContactBody: AddContactRequestModel = {
      personalIdNumber: '' ,
      email:  this.contact.email,
      phoneNumber: this.contact.phoneNumber,
      firstname,
      lastname,
      companyName: this.company.companyName
    };
    const addCompanyBody: AddCompanyRequestModel = {
      orgNumber: this.company.orgNumber,
      companyName: this.company.companyName,
      companyType: this.company.companyType,
      registrationDate: this.company.registrationDate,
      isActive: this.company.isActive,
      type: 'RESELLER'
    };

    const payload = { company: addCompanyBody, contact: addContactBody};
    this.resellerApi.addReseller(payload)
      .subscribe((response) => {
          this.trackingUrl = environment.creddoPage + 'partner/' + response.id;
          this.resellerRegistered = true;
      });
  }

  handleSearchSelected(value: boolean): void {
    if (value) {
      this.companyName.setErrors(null);
    }
    this.searchSelected = value;
  }

  getErrors(control: string): string | null {
    const email = this.email;
    const phoneNumber = this.phoneNumber;
    const companyName = this.companyName;

    switch (control) {
      case 'email':
        if (email.hasError('email') && !email.hasError('required')) {
          return this.translateService.instant('global.error.emailNotValid');
        } else if (email.hasError('required')) {
          return this.translateService.instant('global.error.emailRequired');
        }
        return null;
      case 'phoneNumber':
        if (phoneNumber.hasError('required')) {
          return this.translateService.instant(
            'global.error.phoneNumberRequired'
          );
        } else if (
          (!phoneNumber.hasError('required') &&
            phoneNumber.hasError('invalidPhoneNumber')) ||
          phoneNumber.invalid
        ) {
          return this.translateService.instant(
            'global.error.phoneNumberNotValid'
          );
        }
        return null;
      case 'companyName':
        if (companyName.hasError('required')) {
          return this.translateService.instant(
            'global.error.companyNameOrIdRequired'
          );
        } else if (companyName.hasError('companySearchRequired')) {
          return this.translateService.instant('basicInfo.companySearchRequired');
        }
        return null;
      default:
        return null;
    }
  }
}
